import PerRepoWorkAnalytics from "src/scenes/per-repo/per-repo-work-analytics";
import * as React from "react";
import { ActiveIntegrationsProvider } from "src/scenes/dashboard/active-integrations-provider";
import { Helmet } from "react-helmet";
import RangePickerModal from "src/components/range-picker-modal";
import Box from "@mui/material/Box";
import SaveIcon from "@mui/icons-material/Save";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import { Button, Grid, Stack } from "@mui/material";
import DateRangeIcon from "@mui/icons-material/DateRange";
import styled from "styled-components";
import Portal from "src/components/portal";
import DemoBarPersonal from "src/components/demo-bar-personal";
import DemoBarDemo from "src/components/demo-bar-demo";
import RepoChipAutoComplete from "src/scenes/per-repo/components/repo-chip-select";
import printDateRange from "src/components/print-daterange";
import request from "src/utils/request";
import { useAuth } from "src/components/providers/auth-context";
import SaveGroupModal from "src/scenes/per-repo/components/save-group-modal";
import RepoViewPlaceholder from "src/scenes/per-repo/components/repo-view-placeholder";

const DatepickerButton = styled(Button)`
    background-color: ${({ theme }) => theme.palette.background.default};
    font-size: 12px;
    z-index: 10;
`;

const DateHelper = styled.h2`
    font-size: 11px;
    font-style: normal;
    font-weight: 200;
    opacity: 0.9;
    margin: 0;
`;

const RepoView = () => {
    const [openDatepicker, setOpenDatepicker] = React.useState(false);
    const [openGroupSaveModal, setOpenGroupSaveModal] = React.useState(false);
    const [repos, setRepos] = React.useState([]);
    const [groups, setGroups] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [selectedRepos, setSelectedRepos] = React.useState<string[] | undefined>(undefined);
    const [selectedGroups, setSelectedGroups] = React.useState<string[] | undefined>(undefined);
    const [paramRepos, setParamRepos] = React.useState<string[] | undefined>(undefined);
    const [paramGroups, setParamGroups] = React.useState<string[] | undefined>(undefined);
    const { isDemo } = useAuth();
    const toggleDatepicker = () => setOpenDatepicker(!openDatepicker);
    const toggleSaveModal = () => setOpenGroupSaveModal(!openDatepicker);

    const getRepos = async () => {
        let response;
        if (isDemo) {
            // @ts-expect-error expected
            response = (await import("./components/per-repo-mock-data")).default;
        } else {
            response = await request(`api/data/repos`, { method: "GET" });
        }
        setRepos(response.repos);
        setGroups(response.groups);
        setLoading(false);
    };

    React.useEffect(() => {
        setLoading(true);
        getRepos();
    }, []);

    const onClickRequestTrigger = () => {
        setParamRepos(selectedRepos);
        setParamGroups(selectedGroups);
    };
    return (
        <ActiveIntegrationsProvider>
            <Helmet>
                <title>Adadot - Repo View</title>
            </Helmet>
            <Portal>
                <DemoBarPersonal />
                <DemoBarDemo />
            </Portal>
            <Box sx={{ width: "100%", typography: "body1" }}>
                <Grid container direction="row" alignItems="center" justifyContent="space-between">
                    <Grid item md={10}>
                        <Grid
                            container
                            direction="row"
                            alignItems="center"
                            justifyContent="flex-start"
                            spacing={2}
                        >
                            <Grid item md={6}>
                                <RepoChipAutoComplete
                                    repos={repos}
                                    groups={groups}
                                    setSelectedGroups={setSelectedGroups}
                                    setSelectedRepos={setSelectedRepos}
                                    getRepos={getRepos}
                                    loading={loading}
                                />
                            </Grid>
                            <Grid item>
                                <Button
                                    variant="outlined"
                                    startIcon={<AnalyticsIcon />}
                                    onClick={onClickRequestTrigger}
                                >
                                    Load Dashboard
                                </Button>
                            </Grid>

                            <Grid item>
                                <Button
                                    variant="outlined"
                                    startIcon={<SaveIcon />}
                                    disabled={!selectedRepos || selectedRepos.length < 2}
                                    onClick={toggleSaveModal}
                                >
                                    Save as Group
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item>
                        <Grid container alignItems="center" justifyContent="space-between">
                            <Grid item>
                                <Stack direction="column" marginRight={1}>
                                    <Stack
                                        direction="column"
                                        justifyContent="center"
                                        alignItems="center"
                                    >
                                        <DatepickerButton
                                            startIcon={<DateRangeIcon />}
                                            onClick={toggleDatepicker}
                                        >
                                            Select Dates
                                        </DatepickerButton>
                                        <DateHelper>{printDateRange()}</DateHelper>
                                    </Stack>
                                </Stack>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
                {!paramRepos && !paramGroups && <RepoViewPlaceholder />}
                <PerRepoWorkAnalytics
                    repos={repos}
                    paramRepos={paramRepos}
                    paramGroups={paramGroups}
                    isDemo={isDemo}
                />
            </Box>
            <RangePickerModal
                open={openDatepicker}
                handleClose={() => setOpenDatepicker(false)}
                toggleDatepicker={toggleDatepicker}
                isRepo
                isTeams={false}
            />
            <SaveGroupModal
                open={openGroupSaveModal}
                isDemo={isDemo}
                selectedRepos={selectedRepos}
                getRepos={getRepos}
                handleClose={() => setOpenGroupSaveModal(false)}
            />
        </ActiveIntegrationsProvider>
    );
};

export default RepoView;
